import { FormEvent, KeyboardEvent, ChangeEvent, useState, useEffect, useRef, TouchEvent } from 'react';
import styles from './ActiveScenarioSessionInputBar.module.css';
import { sendIcon } from '../../assets/icons';
import { PiBookOpenTextFill } from "react-icons/pi";

interface ActiveScenarioSessionInputBarProps {
  message: string;
  setMessage: (message: string) => void;
  handleSend: (e: FormEvent) => Promise<void>;
  isSubmitting: boolean;
  onBookClick?: () => void;
  showBookGlow?: boolean;
  glowType?: 'success' | 'error' | 'info';
}

function ActiveScenarioSessionInputBar({ 
  message, 
  setMessage, 
  handleSend,
  isSubmitting,
  onBookClick,
  showBookGlow = false,
  glowType = 'info'
}: ActiveScenarioSessionInputBarProps): JSX.Element {
  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  const [showButton, setShowButton] = useState<boolean>(false);
  const [isButtonVisible, setIsButtonVisible] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Check if the device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Handle button visibility based on message content
  useEffect(() => {
    if (message.trim()) {
      setShowButton(true);
      // Small delay to ensure the animation plays
      setTimeout(() => setIsButtonVisible(true), 10);
    } else {
      setIsButtonVisible(false);
      // Wait for fade out animation to complete before removing from DOM
      setTimeout(() => setShowButton(false), 200);
    }
  }, [message]);

  const handleMessageSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (isSubmitting || !message.trim()) return;
    
    try {
      // Store the current message before clearing
      const currentMessage = message;
      
      // Clear the message first to reset the UI
      clearMessage(e.target as HTMLFormElement);
      
      // Then send the stored message
      await handleSend(e);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const clearMessage = (formElement: HTMLFormElement): void => {
    setMessage('');
    
    // Use the ref for more reliable access to the textarea
    const textarea = textareaRef.current;
    if (textarea) {
      // Explicitly reset height to default minimum
      textarea.style.height = 'auto';
      
      // Then set to the CSS variable
      setTimeout(() => {
        if (textarea) {
          textarea.style.height = 'var(--textarea-min-height)';
          textarea.style.overflowY = 'hidden';
          textarea.scrollTop = 0;
        }
      }, 0);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter') {
      if (window.innerWidth <= 480 || e.shiftKey) {
        return;
      }
      e.preventDefault();
      handleMessageSubmit(e as unknown as FormEvent);
    }
  };

  const handleTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setMessage(e.target.value);
    adjustTextareaHeight(e.target);
  };

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement): void => {
    // Store the current scroll position
    const scrollTop = textarea.scrollTop;
    
    // Reset height to minimum to properly calculate the new height
    textarea.style.height = 'var(--textarea-min-height)';
    
    const computedStyle = window.getComputedStyle(textarea);
    const minHeight = parseInt(computedStyle.getPropertyValue('--textarea-min-height'));
    const maxHeight = parseInt(computedStyle.getPropertyValue('--textarea-max-height'));
    
    // Calculate new height based on content
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    
    if (newHeight > minHeight) {
      if (newHeight === maxHeight) {
        textarea.style.height = 'var(--textarea-max-height)';
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = 'hidden';
      }
    } else {
      textarea.style.height = 'var(--textarea-min-height)';
      textarea.style.overflowY = 'hidden';
    }
    
    // Restore scroll position if needed
    textarea.scrollTop = scrollTop;
  };

  // Simplified touch handlers for mobile - prevent all vertical scrolling
  const handleTouchStart = (e: TouchEvent<HTMLTextAreaElement>): void => {
    if (isMobile) {
      // Just prevent the default behavior on mobile
      e.stopPropagation();
    }
  };

  const handleTouchMove = (e: TouchEvent<HTMLTextAreaElement>): void => {
    // Don't prevent default scroll behavior on the textarea
    // This allows users to scroll within the textarea
    e.stopPropagation();
  };

  // We keep the touch end handler simple
  const handleTouchEnd = (): void => {
    // No special behavior needed
  };

  // Stop spinning when isSubmitting changes to false
  useEffect(() => {
    if (!isSubmitting) {
      setIsSpinning(false);
    }
  }, [isSubmitting]);

  // Add resize handler to adjust textarea height when window is resized
  useEffect(() => {
    const handleResize = () => {
      if (textareaRef.current && message.trim()) {
        // Re-adjust the textarea height on resize
        adjustTextareaHeight(textareaRef.current);
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [message]);

  // Add effect to properly reset textarea when message is cleared
  useEffect(() => {
    if (!message && textareaRef.current) {
      textareaRef.current.style.height = 'var(--textarea-min-height)';
      textareaRef.current.style.overflowY = 'hidden';
    }
  }, [message]);

  // Add effect to maintain textarea height when send button visibility changes
  useEffect(() => {
    if (textareaRef.current) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        if (textareaRef.current) {
          adjustTextareaHeight(textareaRef.current);
        }
      }, 10);
    }
  }, [showButton, isButtonVisible]);

  // Handle textarea focus to ensure scrollability on mobile
  const handleFocus = () => {
    if (textareaRef.current && textareaRef.current.scrollHeight > textareaRef.current.clientHeight) {
      textareaRef.current.style.overflowY = 'auto';
    }
  };

  // Get the appropriate glow class based on the type
  const getGlowClass = () => {
    if (!showBookGlow) return '';
    
    switch (glowType) {
      case 'success':
        return styles.bookButtonGlowSuccess;
      case 'error':
        return styles.bookButtonGlowError;
      case 'info':
      default:
        return styles.bookButtonGlow;
    }
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleMessageSubmit} className={styles.inputArea}>
        {showBookGlow && (
          <div className={`${styles.bookGlowHighlight} ${styles[`highlight${glowType.charAt(0).toUpperCase() + glowType.slice(1)}`]}`}></div>
        )}
        <button 
          type="button"
          className={`${styles.bookButton} ${getGlowClass()}`}
          onClick={onBookClick}
          disabled={isSubmitting}
        >
          <PiBookOpenTextFill className={styles.bookIcon} />
        </button>
        <div style={{ position: 'relative', flex: 1 }}>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            onFocus={handleFocus}
            placeholder="Type your message..."
            className={`${styles.messageInput} ${isMobile ? '' : ''}`}
            disabled={isSubmitting}
            rows={1}
          />
          {/* Always maintain space for the send button */}
          <div className={styles.sendButtonPlaceholder}></div>
          {showButton && (
            <button 
              type="submit"
              className={`${styles.sendButton} ${styles.sendButtonActive} ${isSpinning ? styles.spinning : ''} ${!isButtonVisible ? styles.fadeOut : ''}`}
              disabled={isSubmitting}
            >
              <img src={sendIcon} alt="Send" />
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default ActiveScenarioSessionInputBar; 